/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import './styles/index.css';
import * as React from 'react';
import { createRoot } from 'react-dom/client';
import { AppContainer, EntryPoint, EntryPoints } from './AppContainer';
import { loadStringTable } from './intl/Strings';
import {
  FluentProvider,
  makeStyles,
} from '@fluentui/react-components';
import { DevButton } from './components/DevButton';
import { useOfficeTheme } from './hooks/useOfficeTheme';
import { ProgrammableSurfacesCommunication } from './communication/ProgrammableSurfacesCommunication';
import { adaptiveCardsPropsPrefix, type AdaptiveCardsSchema } from './components/AdaptiveCards';

declare let Office: any;

const useStyles = makeStyles({
  fab: {
    position: 'absolute',
    bottom: '20px',
    right: '20px',
    zIndex: 1000,
  },

});

// Get entry point from Office context settings
const getEntryPoint = (): EntryPoint | undefined => {
  const settings = Office.context?.document?.settings;
  if (settings) {
    const entryPoint = settings.get('entryPoint');
    if (entryPoint && (EntryPoints as readonly string[]).includes(entryPoint)) {
      return entryPoint as EntryPoint;
    }
  }
  return undefined;
};

const isDevModeEnabled = (): boolean => {
  const settings = Office.context?.document?.settings;
  if (settings) {
    const devMode = settings.get('isDevMode');
    return !!devMode;
  }
  return false;
};

const getDisplayLanguage = (): string | undefined => {
  return Office.context?.displayLanguage;
};

const getAdaptiveCardPropsFromSettings = (): Record<string, unknown> => {
  const settings = Office.context?.document?.settings;
  if (!settings) {
    return {};
  }

  const keys: string[] = typeof settings.getKeys === 'function' ? settings.getKeys() : [];
  return keys.reduce<Record<string, unknown>>((acc, key) => {
    if (key.startsWith(adaptiveCardsPropsPrefix)) {
      acc[key] = settings.get(key);
    }
    return acc;
  }, {});
};

export interface MainUIProps {
  entryPoint?: EntryPoint;
  showDevButton?: boolean;
  adaptiveCardsSchema?: AdaptiveCardsSchema;
  [key: string]: unknown;
}

export const MainUI: React.FC<MainUIProps> = ({
  entryPoint: initialEntryPoint,
  showDevButton,
  ...restProps
}) => {
  const [entryPoint, setEntryPoint] = React.useState(initialEntryPoint);
  const styles = useStyles();
  const { theme, backgroundColor } = useOfficeTheme();

  React.useEffect(() => {
    setEntryPoint(initialEntryPoint);
  }, [initialEntryPoint]);

  const adaptiveCardProps = React.useMemo<Record<string, unknown> | undefined>(() => {
    if (entryPoint !== 'adaptiveCards') {
      return undefined;
    }

    const fromProps = Object.keys(restProps).reduce<Record<string, unknown>>((acc, key) => {
      const value = restProps[key];
      if (key.startsWith(adaptiveCardsPropsPrefix) && value !== undefined) {
        acc[key] = value;
      }
      return acc;
    }, {});

    const hasProp = Object.keys(fromProps).length > 0;
    return hasProp ? fromProps : getAdaptiveCardPropsFromSettings();
  }, [entryPoint, restProps]);

  return (
    <FluentProvider
      theme={theme}
      style={{ backgroundColor: backgroundColor }}
    >
      <AppContainer entryPoint={entryPoint} adaptiveCardProps={adaptiveCardProps} />
      {showDevButton && (
        <div className={styles.fab}>
          <DevButton onEntryPointChange={setEntryPoint} />
        </div>
      )}
    </FluentProvider>
  );
};

Office.onReady(async () => {
  // Load the user's language strings
  const displayLanguage = getDisplayLanguage();
  if (displayLanguage) {
    await loadStringTable(window.location.origin, displayLanguage);
  }

  // Initialize communication privateApi
  ProgrammableSurfacesCommunication.getInstance();

  // Get entry point after Office is ready
  const entryPoint = getEntryPoint();
  const devModeEnabled = isDevModeEnabled();

  const appElement = document.getElementById('app');
  if (appElement) {
    const root = createRoot(appElement);
    root.render(<MainUI entryPoint={entryPoint} showDevButton={devModeEnabled} />);
  }
});
